import TimeHelper from '../../utils/time';

export interface SearchOrdersByIdentifiersOrderDto {
    orderNumber: string;
    shipmentNo: string;
    orderDate: string;
    shippedDate: string;
    cargoCompanyName: string;
    shippingListStatusId: number;
    shippingListStatusName?: string | null;
    cityName: string;
    countyName: string;
    neighborhoodName?: string | null;
    grandTotal: number;
    deliveryMethodName: string;
    deliveredDate: string;
    lastUpdatedDate: string;
    platform: string;
    packageCount: number;
    storeCode: string | null;
    paymentMethodName: string;
    paymentMethodId: number;
    shipmentOwner: string;
    storeName?: string | null;
    dPackageCargoShipmentCode?: string | null;
    trackingCode?: string | null;
    customerId?: number | null;
    isScr?: boolean | null;
}

export interface SearchOrdersByIdentifiersResponseDto {
    foundOrders: SearchOrdersByIdentifiersOrderDto[];
    notFoundIdentifiers: string[];
    totalRequested: number;
    totalFound: number;
    totalNotFound: number;
    /** Rule true → SP path (StatusName); false/null → servis path (StatusId). */
    useSpForBulkShipmentSearch?: boolean | null;
}

export type SearchOrdersSummary = {
    totalRequested: number;
    totalFound: number;
    totalNotFound: number;
};

export interface PaymentConsolidationRow {
    orderNumber: string;
    shippingListNo: string;
    orderDateStr: string;
    shippedDateStr: string;
    deliveredDateStr: string;
    lastUpdatedDateStr: string;
    cargoCompanyName: string;
    shippingListStatusId: number;
    shippingListStatusName: string;
    cityName: string;
    countyName: string;
    neighborhoodName: string;
    deliveryMethodName: string;
    shipmentOwner: string;
    storeCode: string;
    platform: string;
    packageCount: string;
    paymentAmount: number | string;
    paymentMethodName: string;
    paymentMethodId: number;
    storeName: string;
    dPackageCargoShipmentCode: string;
    trackingCode: string;
    customerId: number | null;
    isScr: boolean;
}

const toDisplayValue = (value: unknown): string => {
    if (value == null || value === '') {
        return '-';
    }

    return String(value);
};

const toFormattedDate = (value: unknown): string => {
    if (value == null || value === '') {
        return '-';
    }

    const formatted = TimeHelper.formatDateHourWithTimezone(value);
    return formatted || '-';
};

const isPreformattedDate = (value: unknown): boolean => {
    if (typeof value !== 'string') {
        return false;
    }

    return value.includes('.') || value.includes('-') || value.includes('/');
};

const toDateDisplayValue = (rawValue: unknown): string => {
    if (rawValue == null || rawValue === '') {
        return '-';
    }

    return isPreformattedDate(rawValue) ? toDisplayValue(rawValue) : toFormattedDate(rawValue);
};

export const hasPaymentConsolidationCustomerId = (customerId: unknown): boolean => {
    if (customerId == null || customerId === '') {
        return false;
    }

    if (typeof customerId === 'number' && customerId <= 0) {
        return false;
    }

    return true;
};

export const resolveScrTranslatedLabel = (
    customerId: unknown,
    isScr: boolean | null | undefined,
    labels: { yes: string; no: string }
): string => {
    if (!hasPaymentConsolidationCustomerId(customerId)) {
        return '-';
    }

    return isScr === true ? labels.yes : labels.no;
};

/** null/undefined → false (servis path). */
export const resolveUseSpForBulkShipmentSearch = (
    value: boolean | null | undefined
): boolean => value === true;

export const resolveStatusDisplayName = (
    item: Pick<SearchOrdersByIdentifiersOrderDto, 'shippingListStatusName'>,
    useSp: boolean
): string => {
    if (useSp) {
        return toDisplayValue(item.shippingListStatusName);
    }

    return toDisplayValue(item.shippingListStatusName);
};

export const mapSearchOrdersByIdentifiersItem = (
    item: SearchOrdersByIdentifiersOrderDto,
    useSpForBulkShipmentSearch?: boolean | null
): PaymentConsolidationRow => {
    const useSp = resolveUseSpForBulkShipmentSearch(useSpForBulkShipmentSearch);
    const customerId = item.customerId ?? null;
    const isScr = item.isScr === true;

    return {
        orderNumber: toDisplayValue(item.orderNumber),
        shippingListNo: toDisplayValue(item.shipmentNo),
        orderDateStr: toDateDisplayValue(item.orderDate),
        shippedDateStr: toDateDisplayValue(item.shippedDate),
        deliveredDateStr: toDateDisplayValue(item.deliveredDate),
        lastUpdatedDateStr: toDateDisplayValue(item.lastUpdatedDate),
        cargoCompanyName: toDisplayValue(item.cargoCompanyName),
        shippingListStatusId: item.shippingListStatusId,
        shippingListStatusName: resolveStatusDisplayName(item, useSp),
        cityName: toDisplayValue(item.cityName),
        countyName: toDisplayValue(item.countyName),
        neighborhoodName: toDisplayValue(item.neighborhoodName),
        deliveryMethodName: toDisplayValue(item.deliveryMethodName),
        shipmentOwner: toDisplayValue(item.shipmentOwner),
        storeCode: toDisplayValue(item.storeCode),
        platform: toDisplayValue(item.platform),
        packageCount: toDisplayValue(item.packageCount),
        paymentAmount: item.grandTotal ?? '-',
        paymentMethodName: toDisplayValue(item.paymentMethodName),
        paymentMethodId: item.paymentMethodId,
        storeName: toDisplayValue(item.storeName),
        dPackageCargoShipmentCode: toDisplayValue(item.dPackageCargoShipmentCode),
        trackingCode: toDisplayValue(item.trackingCode),
        customerId,
        isScr,
    };
};

export const extractUseSpForBulkShipmentSearch = (
    responseData: SearchOrdersByIdentifiersResponseDto | undefined
): boolean => resolveUseSpForBulkShipmentSearch(responseData?.useSpForBulkShipmentSearch);

export const extractSearchOrdersByIdentifiersResults = (
    responseData: SearchOrdersByIdentifiersResponseDto | undefined
): SearchOrdersByIdentifiersOrderDto[] => {
    if (!responseData?.foundOrders) {
        return [];
    }

    return Array.isArray(responseData.foundOrders) ? responseData.foundOrders : [];
};

export const extractNotFoundIds = (
    responseData: SearchOrdersByIdentifiersResponseDto | undefined
): string[] => {
    if (!responseData?.notFoundIdentifiers) {
        return [];
    }

    return Array.isArray(responseData.notFoundIdentifiers) ? responseData.notFoundIdentifiers : [];
};

export const extractSearchSummary = (
    responseData: SearchOrdersByIdentifiersResponseDto | undefined
): SearchOrdersSummary | null => {
    if (!responseData) {
        return null;
    }

    return {
        totalRequested: responseData.totalRequested ?? 0,
        totalFound: responseData.totalFound ?? 0,
        totalNotFound: responseData.totalNotFound ?? 0,
    };
};
